// Better Stack client-side telemetry.
//
// Reads `process.env` directly because `lib/config/env.ts` validates
// eagerly at module import, and we need conditional init (skip when
// token is absent). This is the ONLY place outside `lib/config/env.ts`
// where direct `process.env` access for telemetry tokens is allowed.
//
// ─── Enablement gate ───────────────────────────────────────────────────
// Telemetry initializes if and only if we're running on Vercel.
// Vercel auto-injects `NEXT_PUBLIC_VERCEL_URL` on every deployment; we
// use that rather than `process.env.VERCEL` because the latter is a
// server-only var that Next.js does NOT inline into the client bundle —
// in the browser it would always evaluate to `undefined`, gating off
// every client-side `reportError` / `trackAction` / `sendLog`. Local
// `pnpm dev`, local `pnpm build && pnpm start`, and CI scripts never
// emit. There is no `TELEMETRY_ENABLED` flag — the gate is environmental.
//
// ─── Network attribution ───────────────────────────────────────────────
// Every event carries a `morfi.network` attribute resolved from the
// zustand network store (`useNetworkStore.getState().current`). One
// Better Stack source receives events from both networks; dashboards
// filter by this attribute. Falls back to `'unknown'` if the store
// hasn't hydrated (extremely rare — store initializes synchronously
// at module load).

import { useNetworkStore } from '@/lib/network/store';

import { normalizeThrown, serializeForTelemetry } from './normalize-error';
import { scrubGrantTokens } from './scrub';
import type { BetterStackWindow, LogLevel, TelemetryUser } from './types';

const TOKEN: string | undefined = process.env.NEXT_PUBLIC_BETTERSTACK_JS_TOKEN;
const ON_VERCEL: boolean = Boolean(process.env.NEXT_PUBLIC_VERCEL_URL);
const APP_VERSION: string = process.env.NEXT_PUBLIC_APP_VERSION ?? 'dev';

// Service identity for browser telemetry. The browser tag is a Sentry-based
// RUM product and cannot emit an OTel Resource, so it can't set the canonical
// `resource.attributes.service.name` the way the server exporter does
// (`instrumentation.node.ts` → 'morfi-frontend'). Without this, every browser
// event — including auto-captured `console.warn`/`error` from third-party libs
// and the SDK — lands with a null service.name, indistinguishable in the
// shared OTLP source. We attribute them via a global scope tag instead (see
// `initializeTelemetry`), which the tag applies to ALL events. `-web`
// distinguishes browser-origin from the server's 'morfi-frontend'.
const SERVICE_NAME = 'morfi-frontend-web';

/** Whether telemetry is configured (token present) and running on Vercel. */
function isEnabled(): boolean {
  return ON_VERCEL && typeof TOKEN === 'string' && TOKEN.length > 0;
}

/**
 * Whether telemetry is live for this session: configured, on Vercel, AND
 * running in the browser. Instrumentation that should not even register
 * its listeners off-Vercel (Web Vitals, soft-nav tracking) gates on this.
 */
export function isTelemetryActive(): boolean {
  return isBrowser() && isEnabled();
}

/** Whether we are running in the browser. */
function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

/** Safe accessor for the Better Stack global. */
function getBetterStack(): BetterStackWindow['betterstack'] {
  if (!isBrowser()) return undefined;
  return (window as BetterStackWindow).betterstack;
}

/** Resolve the user's current network for event tagging. Reads the
 *  zustand store via its vanilla `getState()` API (works outside React).
 *  Returns `'unknown'` if the store throws — defensive only; the store
 *  is always initialized synchronously at module load. */
function resolveClientNetwork(): string {
  try {
    return useNetworkStore.getState().current;
  } catch {
    return 'unknown';
  }
}

/** Public accessor for the current network tag, for non-error transports
 *  (e.g. the Web Vitals / soft-nav beacons) that tag samples by network. */
export function clientNetworkTag(): string {
  return resolveClientNetwork();
}

/**
 * Returns the inline script string that bootstraps the Better Stack JS tag.
 * The caller (TelemetryInitializer) injects this into a `<Script>` component.
 * Returns `null` when the token is absent or we're not on Vercel.
 */
export function initializeTelemetry(): string | null {
  if (!isEnabled()) return null;

  // Validate token only contains safe characters before interpolating
  // into the inline script (prevents XSS via env var injection).
  if (!TOKEN || !/^[a-zA-Z0-9_-]+$/.test(TOKEN)) return null;

  // Source-level `environment` tag distinguishes production from preview
  // deploys at the Better Stack source dashboard. Network is added per-
  // event via `morfi.network`, not per-source.
  const environment: string = process.env.VERCEL_ENV ?? 'production';
  if (!/^[a-zA-Z0-9_-]+$/.test(environment)) return null;

  const version: string = /^[a-zA-Z0-9_-]+$/.test(APP_VERSION) ? APP_VERSION : 'dev';

  return [
    '!function(b,e,t,r){',
    'b[t]=b[t]||function(...args){(b[t].q=b[t].q||[]).push(args)};',
    'b[t].l=+new Date;',
    // SECURITY: betterstack.net/b.js is loaded without SRI because Better Stack
    // updates this script dynamically (their standard installation pattern). SRI
    // would break on every update. Self-hosting would provide integrity guarantees
    // but requires manual version management. Accepted risk.
    "var s=e.createElement('script');s.async=1;s.crossOrigin='anonymous';",
    "s.src='https://betterstack.net/b.js?t='+r;",
    "(e.head||e.getElementsByTagName('head')[0]).appendChild(s);",
    `}(window,document,'betterstack','${TOKEN}');`,
    // Sentry options pass through the documented `sentry:` key (Better Stack
    // js-tag install docs). `initialScope.tags` is Sentry's base scope that
    // merges into every event, so the `service` tag attaches to manual
    // `track`/`captureException` AND auto-captured `console` records — giving
    // browser logs a queryable service identity (the closest achievable to
    // `service.name` from a RUM tag, which cannot emit an OTel Resource).
    `betterstack('init',{environment:'${environment}',version:'${version}',console:{level:'warn'},sentry:{initialScope:{tags:{service:'${SERVICE_NAME}'}}}});`,
  ].join('');
}

/**
 * Report an error to Better Stack. Wraps non-Error values in an Error.
 * No-op when the token is absent or running server-side.
 */
// ALLOW-UNKNOWN: error reporter must accept any thrown value
export function reportError(error: unknown, context?: Record<string, string>): void {
  if (!isBrowser() || !isEnabled()) return;

  const bs = getBetterStack();
  if (!bs) return;

  // Scrub grant tokens (spec 048 FR-035..FR-038/FR-067) — defence in
  // depth so an accidental token in an error message or context value
  // cannot leak to Better Stack.
  // ALLOW-UNKNOWN: type-narrowed at use-site / mock signature passthrough.
  const scrubbedError: unknown = scrubGrantTokens(error);
  const scrubbedContext: Record<string, string> | undefined =
    context !== undefined ? scrubGrantTokens(context) : undefined;

  // Normalize any thrown value into a real Error so Better Stack formats
  // it correctly. Critically, we ALSO flatten the original (pre-normalize)
  // value into `error_*` context fields so structured payloads from the
  // SDK (e.g. PersistenceShapeViolationError carries `category`, `path`,
  // `kind`) survive as searchable columns instead of being collapsed to
  // the literal string "[object Object]". See `./normalize-error.ts`.
  const normalised: Error = normalizeThrown(scrubbedError);
  const errorFields: Record<string, string> = serializeForTelemetry(scrubbedError);
  // Collect runtime context — all zero-overhead reads from existing browser APIs
  const nav = navigator as {
    connection?: { effectiveType?: string; downlink?: number };
    deviceMemory?: number;
  };
  const perf = performance as { memory?: { usedJSHeapSize?: number; totalJSHeapSize?: number } };

  const extra: Record<string, string> = {
    ...scrubbedContext,
    // Structured payload of the original thrown value. Each own property
    // lands as its own `error_<key>` field in the event's `extra` context
    // (e.g. `extra.error_category=PERSISTENCE_SCHEMA_MISMATCH`). Splatted
    // BEFORE the per-event Morfi attributes so the latter win on collisions.
    ...errorFields,
    // Per-event Morfi attributes
    morfi_network: resolveClientNetwork(),
    // Page context
    url: window.location.pathname,
    full_url: window.location.href,
    route: window.location.pathname.split('/').slice(0, 4).join('/'),
    referrer: document.referrer || 'direct',
    // Device & network
    viewport: `${String(window.innerWidth)}x${String(window.innerHeight)}`,
    device_pixel_ratio: String(window.devicePixelRatio),
    connection: nav.connection?.effectiveType ?? 'unknown',
    downlink_mbps: String(nav.connection?.downlink ?? 'unknown'),
    device_memory_gb: String(nav.deviceMemory ?? 'unknown'),
    online: String(navigator.onLine),
    // Performance
    js_heap_used_mb: perf.memory?.usedJSHeapSize
      ? String(Math.round(perf.memory.usedJSHeapSize / 1_048_576))
      : 'unknown',
    js_heap_total_mb: perf.memory?.totalJSHeapSize
      ? String(Math.round(perf.memory.totalJSHeapSize / 1_048_576))
      : 'unknown',
    time_on_page_s: String(Math.round(performance.now() / 1000)),
    // i18n
    locale: document.documentElement.lang || 'unknown',
    timestamp_local: new Date().toISOString(),
    app_version: APP_VERSION,
  };

  if (bs.Sentry?.getClient() !== undefined) {
    bs.Sentry.captureException(normalised, { extra });
    return;
  }
  // `b.js` loads async and its stub queue replays only tag commands, so an
  // error raised before the tag's Sentry client exists travels as an
  // error-level log instead.
  sendLog('report_error_before_sdk_load', 'error', {
    ...extra,
    error_name: normalised.name,
    error_message: normalised.message,
  });
}

/** Error/failure keywords — events containing these are auto-tagged as error level. */
const ERROR_KEYWORDS: string[] = ['error', 'failed', 'failure', 'crash'];
/** Warning keywords — events containing these are auto-tagged as warn level. */
const WARN_KEYWORDS: string[] = ['rejected', 'stale', 'timeout', 'slow', 'retry'];
const EXPORTED_INFO_LOGS: ReadonlySet<string> = new Set([
  'tx.lifecycle',
  // Opportunistic: if the JS tag is present this still ships. The popup skip
  // path does not have the tag — production signal is POST /api/telemetry-beacon.
  'turnkey.popup_callback.skip_provider',
]);

/** Infer severity from an event name. */
function inferLevel(name: string): 'error' | 'warn' | 'info' {
  const lower: string = name.toLowerCase();
  if (ERROR_KEYWORDS.some((kw) => lower.includes(kw))) return 'error';
  if (WARN_KEYWORDS.some((kw) => lower.includes(kw))) return 'warn';
  return 'info';
}

/**
 * Send a structured log to Better Stack.
 *
 * Every log lands as:
 *   { event: "frontend.log", payload: { level, message, morfi_network, url, route, timestamp, ...context } }
 * `tx.lifecycle` fields are the exception: they are emitted at the payload
 * root so they share the mobile OTLP attribute paths.
 *
 * No-op when the token is absent or running server-side. Server-side
 * `logger.*` calls route to `lib/telemetry/server-log.ts` instead.
 */
export function sendLog(message: string, level: LogLevel, context?: Record<string, unknown>): void {
  if (!isBrowser() || !isEnabled()) return;
  if (level === 'info' && !EXPORTED_INFO_LOGS.has(message)) return;

  const bs = getBetterStack();
  if (!bs) return;

  // Scrub grant tokens (spec 048 FR-035..FR-038/FR-067).
  const scrubbedMessage: string = scrubGrantTokens(message);
  const scrubbedContext: Record<string, unknown> | undefined =
    context !== undefined ? scrubGrantTokens(context) : undefined;

  bs('track', 'frontend.log', {
    ...(message === 'tx.lifecycle' ? (scrubbedContext ?? {}) : { context: scrubbedContext ?? {} }),
    level,
    message: scrubbedMessage,
    morfi_network: resolveClientNetwork(),
    url: window.location.pathname,
    route: window.location.pathname.split('/').slice(0, 4).join('/'),
    timestamp: new Date().toISOString(),
    app_version: APP_VERSION,
  });
}

/**
 * Track a custom user/system action (e.g. trade_e2e_completed, tx_rpc_error).
 *
 * Level is auto-inferred from the event name:
 *   - Contains "error"/"failed"/"failure"/"crash" → level: "error"
 *   - Contains "rejected"/"stale"/"timeout"/"slow"/"retry" → level: "warn"
 *   - Everything else → level: "info"
 *
 * Override by passing `level` in the context object.
 *
 * Every event lands as:
 *   { event: "frontend.event", payload: { level, name, morfi_network, url, route, timestamp, ...context } }
 *
 * No-op when the token is absent or running server-side.
 */
export function trackAction(name: string, context?: Record<string, unknown>): void {
  if (!isBrowser() || !isEnabled()) return;

  const level: string = typeof context?.level === 'string' ? context.level : inferLevel(name);
  if (level === 'info') return;

  const bs = getBetterStack();
  if (!bs) return;

  // Scrub grant tokens (spec 048 FR-035..FR-038/FR-067).
  const scrubbedName: string = scrubGrantTokens(name);
  const scrubbedContext: Record<string, unknown> | undefined =
    context !== undefined ? scrubGrantTokens(context) : undefined;

  bs('track', 'frontend.event', {
    level,
    message: scrubbedName,
    morfi_network: resolveClientNetwork(),
    url: window.location.pathname,
    route: window.location.pathname.split('/').slice(0, 4).join('/'),
    timestamp: new Date().toISOString(),
    app_version: APP_VERSION,
    context: scrubbedContext ?? {},
  });
}

/**
 * Identify the current user for session attribution.
 * No-op when the token is absent or running server-side.
 */
export function identifyUser(userData: TelemetryUser): void {
  if (!isBrowser() || !isEnabled()) return;

  const bs = getBetterStack();
  if (!bs) return;

  const sanitised: TelemetryUser = { id: userData.id };
  if (userData.email) {
    sanitised.email = userData.email;
  }
  if (userData.address) {
    sanitised.address = `${userData.address.slice(0, 8)}...`;
  }
  if (userData.username) {
    sanitised.username = userData.username;
  }
  if (userData.all_wallets) {
    sanitised.all_wallets = userData.all_wallets;
  }
  // Scrub grant tokens (spec 048 FR-035..FR-038/FR-067) — final
  // defence in case any string contains a stray token.
  bs('user', scrubGrantTokens(sanitised));
}
